import type { GpuHostOperationsView } from "@dutyon/apis";
export function gpuServerFinalization(
  view: GpuHostOperationsView,
  job: { app: string; jobId: string; attempt: number },
) {
  const evidence = view.serverFinalizations?.find(
    (item) => item.app === job.app && item.jobId === job.jobId && item.attempt === job.attempt,
  );
  return evidence ? (evidence.finalized ? "확정" : "미확정") : "확인 불가";
}
export function gpuHostPresentation(view: GpuHostOperationsView, elapsedMs: number) {
  const now = view.serverTime + Math.max(0, elapsedMs);
  if (!view.configured) return { label: "상태 수집 미설정", color: "gray", stale: false, now };
  if (!view.host) return { label: "첫 상태 수신 대기", color: "gray", stale: true, now };
  const stale =
    now - view.host.receivedAt > view.staleAfterMs || view.host.receivedAt > now + 15000;
  if (stale) return { label: "연결 확인 불가", color: "orange", stale: true, now };
  const observation = view.host.observation;
  if (observation.state === "stopping")
    return { label: "종료 중", color: "gray", stale: false, now };
  if (observation.state === "degraded" || !observation.gpu.ready)
    return { label: "확인 필요", color: "orange", stale: false, now };
  return {
    label: observation.gpu.busy ? "연산 중" : "대기",
    color: observation.gpu.busy ? "blue" : "green",
    stale: false,
    now,
  };
}
export function formatGpuTime(value: number | null | undefined) {
  if (value == null || !Number.isFinite(value) || value <= 0) return "확인 기록 없음";
  return new Intl.DateTimeFormat("ko-KR", {
    timeZone: "Asia/Seoul",
    dateStyle: "short",
    timeStyle: "medium",
  }).format(value);
}
export function formatGpuDuration(ms: number) {
  const seconds = Math.floor(Math.max(0, ms) / 1000);
  return seconds < 60
    ? `${seconds}초`
    : seconds < 3600
      ? `${Math.floor(seconds / 60)}분 ${seconds % 60}초`
      : `${Math.floor(seconds / 3600)}시간 ${Math.floor((seconds % 3600) / 60)}분`;
}
export function formatGpuBytes(value: number | null) {
  return value === null ? "측정 불가" : `${(value / 1024 ** 3).toFixed(1)} GB`;
}
export const gpuJobStages = {
  queued: "대기",
  running: "연산 중",
  result_stored: "Mac 결과 보관",
  failed: "실패",
} as const;
export const gpuFailures = {
  cancelled: "취소",
  timed_out: "시간 초과",
  execution_failed: "연산 실패",
  interrupted: "중단",
  no_valid_candidate: "조건을 충족하는 후보 없음",
} as const;

type Observation = NonNullable<GpuHostOperationsView["host"]>["observation"];
export function gpuStoragePresentation(snapshot: Observation) {
  const disk = snapshot.diskAdmission;
  const retention = snapshot.retention;
  return {
    disk:
      disk === undefined
        ? "제공 안 함"
        : disk === null
          ? "제한 미설정"
          : (
              {
                not_checked: "첫 측정 대기 · 신규 접수 제한",
                available: "공간 기준 충족",
                low_space: "공간 부족 · 신규 접수 제한",
                measurement_failed: "측정 실패 · 신규 접수 제한",
              } as const
            )[disk.reason],
    retention:
      retention === undefined
        ? "제공 안 함"
        : retention === null
          ? "자동 정리 미설정"
          : !retention.started
            ? "정리 중지"
            : !retention.healthy
              ? "정리 오류 · 신규 접수 제한"
              : "자동 정리 활성",
  };
}
